import { ShoppingCartOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, AutoComplete, Button, Card, Col, Form, Input, InputNumber, Row, Table, Typography } from "antd";
import { useMemo, useState } from "react";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatCurrency, formatDateTime, formatMileage } from "../../shared/utils/formatters";
import { operationsApi } from "./operations-api";

export function PurchaseTestPage() {
  const { locale } = useAppConfig();
  const zh = locale === "zh";
  const queryClient = useQueryClient();
  const items = useQuery({ queryKey: ["purchase-items"], queryFn: operationsApi.purchaseItems });
  const receipts = useQuery({ queryKey: ["purchase-receipts"], queryFn: operationsApi.purchaseReceipts });
  const [partName,setPartName] = useState("");
  const [partNumber,setPartNumber] = useState("");
  const [vehicleApplication,setVehicleApplication] = useState("");
  const [quantity,setQuantity] = useState(1);
  const [supplier,setSupplier] = useState("");
  const [reference,setReference] = useState("");
  const [notes,setNotes] = useState("");
  const partNameOptions = useMemo(() => (items.data ?? []).map(item => ({
    key:item.id,
    value:item.name,
    label:`${item.name} · ${item.sku}${item.vehicleApplication ? ` · ${item.vehicleApplication}` : ""}`
  })),[items.data]);
  const applyExistingPart = (itemId:string) => {
    const item = items.data?.find(candidate => candidate.id === itemId);
    if (!item) return;
    setPartName(item.name);
    setPartNumber(item.sku);
    setVehicleApplication(item.vehicleApplication ?? "");
  };
  const receive = useMutation({
    mutationFn:operationsApi.receivePurchase,
    onSuccess:async () => {
      setPartName("");
      setPartNumber("");
      setVehicleApplication("");
      setQuantity(1);
      setSupplier("");
      setReference("");
      setNotes("");
      await Promise.all([
        queryClient.invalidateQueries({queryKey:["purchase-items"]}),
        queryClient.invalidateQueries({queryKey:["purchase-receipts"]}),
        queryClient.invalidateQueries({queryKey:["inventory-test"]})
      ]);
    }
  });

  return <section>
    <div className="page-title business-page-title">
      <div>
        <Typography.Title level={2}>{zh ? "采购" : "Purchase"}</Typography.Title>
        <Typography.Paragraph type="secondary">
          {zh ? "登记采购配件及供应商信息，确认收货后库存数量自动增加。" : "Record purchased parts and supplier details. Receiving automatically updates inventory."}
        </Typography.Paragraph>
      </div>
    </div>
    {receive.isSuccess && <Alert type="success" showIcon closable message={zh ? "采购收货已登记，库存已经更新。" : "Purchase receipt recorded and inventory updated."} />}
    {(receive.isError || items.isError || receipts.isError) && <Alert type="error" showIcon message={String(receive.error ?? items.error ?? receipts.error)} />}
    <Card title={zh ? "采购配件登记" : "Receive purchased part"} className="operations-receipt-card">
      <Form layout="vertical" onFinish={() => partName.trim() && partNumber.trim() && vehicleApplication.trim() && supplier.trim() && receive.mutate({
        partName:partName.trim(),
        partNumber:partNumber.trim(),
        vehicleApplication:vehicleApplication.trim(),
        quantity,
        supplier:supplier.trim(),
        reference:reference.trim() || undefined,
        notes:notes.trim() || undefined
      })}>
        <Row gutter={16}>
          <Col xs={24} sm={12} xl={6}>
            <Form.Item label={zh ? "采购配件名称" : "Part name"} required>
              <AutoComplete
                value={partName}
                onChange={setPartName}
                onSelect={(_,option) => applyExistingPart(String(option.key))}
                options={partNameOptions}
                filterOption={(input,option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                placeholder={zh ? "输入名称或选择已有配件" : "Enter a name or select an existing part"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={4}>
            <Form.Item label={zh ? "配件号" : "Part number"} required>
              <Input
                value={partNumber}
                maxLength={80}
                onChange={event => {
                  const next = event.target.value.toUpperCase();
                  setPartNumber(next);
                  const existing = items.data?.find(item => item.sku.toUpperCase() === next.trim());
                  if (existing) applyExistingPart(existing.id);
                }}
                placeholder={zh ? "输入配件号" : "Enter part number"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={5}>
            <Form.Item label={zh ? "适用车型" : "Vehicle application"} required>
              <Input value={vehicleApplication} maxLength={160} onChange={event => setVehicleApplication(event.target.value)} placeholder={zh ? "例如：2023 Cadillac Escalade" : "e.g. 2023 Cadillac Escalade"} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={3}>
            <Form.Item label={zh ? "数量" : "Quantity"} required>
              <InputNumber min={1} precision={0} value={quantity} onChange={value => setQuantity(value ?? 1)} style={{width:"100%"}} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Form.Item label={zh ? "供应商" : "Supplier"} required>
              <Input value={supplier} maxLength={120} onChange={event => setSupplier(event.target.value)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label={zh ? "采购参考号" : "Purchase reference"}>
              <Input value={reference} maxLength={80} onChange={event => setReference(event.target.value.toUpperCase())} placeholder="PO / RECEIPT #" />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item label={zh ? "备注" : "Notes"}>
              <Input.TextArea value={notes} maxLength={500} autoSize={{minRows:1,maxRows:3}} onChange={event => setNotes(event.target.value)} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" icon={<ShoppingCartOutlined />} loading={receive.isPending} disabled={!partName.trim() || !partNumber.trim() || !vehicleApplication.trim() || !supplier.trim() || quantity < 1}>
          {zh ? "确认收货" : "Receive purchase"}
        </Button>
      </Form>
    </Card>
    <Card title={zh ? "采购记录" : "Purchase history"}>
      <Table
        rowKey="id"
        loading={receipts.isLoading}
        dataSource={receipts.data ?? []}
        pagination={{pageSize:20,showSizeChanger:false}}
        scroll={{x:1300}}
        columns={[
          {title:zh ? "采购配件名称" : "Part name",dataIndex:"partName",key:"partName",fixed:"left",width:200},
          {title:zh ? "数量" : "Quantity",dataIndex:"quantity",key:"quantity",align:"right",width:90,render:formatMileage},
          {title:zh ? "配件号" : "Part number",dataIndex:"partNumber",key:"partNumber",width:150,render:value => <Typography.Text className="mono">{value}</Typography.Text>},
          {title:zh ? "适用车型" : "Vehicle application",dataIndex:"vehicleApplication",key:"vehicleApplication",width:220,render:value => value || "—"},
          {title:zh ? "供应商" : "Supplier",dataIndex:"supplier",key:"supplier",width:180,render:value => value || "—"},
          {title:zh ? "备注" : "Notes",dataIndex:"notes",key:"notes",width:240,render:value => value || "—"},
          {title:zh ? "采购成本" : "Total cost",dataIndex:"totalCost",key:"totalCost",align:"right",width:130,render:formatCurrency},
          {title:zh ? "收货时间" : "Received",dataIndex:"receivedAt",key:"receivedAt",width:180,render:formatDateTime}
        ]}
      />
    </Card>
  </section>;
}
